import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ProposalSummaryGetRequest,
  ProposalSummaryMoveToApplicationRequest,
  Result,
  ResultStates
} from 'bff/client';
import { UseDisplayErrorMutationOptions } from 'bff/types/display-error-props.type';
import { clientService } from 'stores/client-store/client-store';
import { useInvalidateProposalInformation } from '../common/ProposalsInfosApi';

export const proposalSummaryKeys = {
  base: () => ['proposal-details'] as const,
  fetch: (request: ProposalSummaryGetRequest) => [...proposalSummaryKeys.base(), request]
};

export const useInvalidateProposalSummaryDetails = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: proposalSummaryKeys.base(),
      refetchType: 'all'
    });
};

export const getProposalSummaryDetails = (request: ProposalSummaryGetRequest) => {
  const { proposalSummaryApi } = clientService.getApiClient();

  return queryOptions({
    queryKey: proposalSummaryKeys.fetch(request),
    queryFn: () => proposalSummaryApi.proposalSummaryGet(request),
    select: (response) => response.data!
  });
};

export const useProposalToApplication = (
  options?: UseDisplayErrorMutationOptions<Result, ProposalSummaryMoveToApplicationRequest>
) => {
  const { proposalSummaryApi } = clientService.getApiClient();
  const invalidateFormState = useInvalidateProposalInformation();
  const invalidateProposalSummaryDetails = useInvalidateProposalSummaryDetails();

  return useMutation({
    ...options,
    mutationKey: ['proposal-summary-post'],
    mutationFn: (proposalSummaryApplicationRequest: ProposalSummaryMoveToApplicationRequest) =>
      proposalSummaryApi.proposalSummaryMoveToApplication(proposalSummaryApplicationRequest),
    onSuccess: async (data, variables, context) => {
      if (data.resultState === ResultStates.DisplayError) {
        options?.onDisplayError?.(data);
      } else {
        await invalidateFormState();
        await invalidateProposalSummaryDetails();
        options?.onSuccess?.(data, variables, context);
      }
    }
  });
};
